// Vaanmathi
import React, { useState, useEffect } from "react";
import { FaHistory } from "react-icons/fa";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

function HistoryRecord() {
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");

      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  // Fetch data from API
  useEffect(() => {
    const fetchHistoryData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_PayrollProduct_PayrollProductHistory_PayrollProductHistoryRecord"
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const dataArray = Array.isArray(data.reverse()) ? data : [data];

        setHistoryData(dataArray);
        setFilteredData(dataArray);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const userId =
        localStorage.getItem("userId") || sessionStorage.getItem("userId");

      if (!userId) {
        navigate("/signin");
      }
    };

    checkAuth();
  }, [navigate]);

  // Handle search functionality
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredData(historyData);
    } else {
      const filtered = historyData.filter((record) =>
        Object.values(record).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, historyData]);

  // Format date for display
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <div className="container mt-5 pt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5 pt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>Failed to load history records: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-5">
      {/* Header Section - unchanged as requested */}
      <div className="d-flex justify-content-center align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 text-dark fw-bold">
            <FaHistory className="me-2" size={28} />
            History Records
          </h1>
        </div>
      </div>

      {/* Search Bar */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <Search size={18} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control p-2"
              placeholder="Search by name, mobile number, or employee ID..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Improved Cards View */}
      {filteredData.length === 0 ? (
        <div className="text-center py-5">
          <div className="card shadow-sm border-0">
            <div className="card-body py-5">
              <h5 className="text-muted mb-3">No records found</h5>
              {searchTerm && (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={clearSearch}
                >
                  Clear search and show all records
                </button>
              )}
            </div>
          </div>
        </div>
      ) : isMobile ? (
        <div>
          {filteredData.map((record, index) => (
            <div key={record.id || index} className="mb-3">
              <div
                className="card shadow-sm"
                style={{ textAlign: "left", borderRadius: "12px" }}
              >
                <div className="card-body">
                  <table className="table table-striped table-bordered mb-2">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Name</td>
                        <td>{record.employeeName}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Employee ID</td>
                        <td>{record.employeeId}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Mobile</td>
                        <td>{record.employeeMobile}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Component</td>
                        <td>{record.component}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Change Type</td>
                        <td>{record.changeType}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">From</td>
                        <td>{record.oldValue}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">To</td>
                        <td>{record.newValue}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Date & Time</td>
                        <td>{formatDate(record.currentdate)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Changed By</td>
                        <td>{record.name}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Source Mobile</td>
                        <td>{record.sourcemobile}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3">
          {filteredData.map((record, index) => (
            <div key={record.id || index} className="col-12">
              <div className="card shadow-sm border-0 hover-shadow transition-all">
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    {/* Main Info Column */}
                    <div className="col-lg-4 mb-3 mb-lg-0">
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-light rounded p-2 me-3">
                          <FaHistory className="text-primary" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-semibold">
                            {record.employeeName}
                          </h6>
                          <small className="text-muted">
                            ID: {record.employeeId}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-light text-dark">
                          <i className="fas fa-mobile-alt me-1"></i>{" "}
                          {record.employeeMobile}
                        </span>
                      </div>
                    </div>
                    {/* Change Details Column */}
                    <div className="col-lg-5 mb-3 mb-lg-0">
                      <div className="d-flex flex-column flex-md-row gap-3">
                        <div>
                          <small className="text-muted d-block">
                            Component
                          </small>
                          <span className="badge bg-light bg-opacity-10 text-dark">
                            {record.component}
                          </span>
                        </div>
                        <div>
                          <small className="text-muted d-block">
                            Change Type
                          </small>
                          <span className="badge bg-light bg-opacity-10 text-dark">
                            {record.changeType}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 d-flex flex-column flex-md-row gap-3">
                        <div>
                          <small className="text-muted d-block">From</small>
                          <span className="badge bg-light text-dark">
                            {record.oldValue}
                          </span>
                        </div>
                        <div className="text-center d-none d-md-block">
                          <i className="fas fa-arrow-right text-muted mt-3"></i>
                        </div>
                        <div>
                          <small className="text-muted d-block">To</small>
                          <span className="badge bg-success bg-opacity-10 text-success">
                            {record.newValue}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Date Column */}
                    <div className="col-lg-3 text-lg-end">
                      <small className="text-muted d-block">Date & Time</small>
                      <div className="fw-medium text-dark">
                        {formatDate(record.currentdate)}
                      </div>
                      <span className="badge bg-light text-dark m-1">
                        <i className="fas fa-user-edit me-1"></i> {record.name}
                      </span>
                      <small className="badge bg-light text-dark m-1">
                        <i className="fas fa-mobile-alt me-1"></i>
                        {record.sourcemobile}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HistoryRecord;
